import { getTypedAm5 } from '@/Util/XertAm5';
import { xssColors } from '@/Common/constants';
import { RACE_EVENT_COLORS } from '@/Common/activityTagOptions';
import { dateToChartTime, type ChartBreakthrough, type ChartDay, type ChartMarker, type MarkerKind, type Signature } from './chartData';
import { MARKER_KIND_ORDER, MARKER_KINDS } from './seriesConfig';

// Pieces the history chart and the stacked signature charts share. They need amCharts loaded
// (mount the chart inside <Am5Loader>), so the modules are read when called, not at import.

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * How a breakthrough diamond is drawn. One that earned a medal is filled in the medal's colour. One
 * that didn't -- a near breakthrough -- is an open diamond: pale fill, dark outline, as on the old chart.
 */
function diamondLook(item: { medal?: string }) {
  const { am5 } = getTypedAm5();
  return item.medal
    ? { fill: am5.color(item.medal), fillOpacity: 0.9, stroke: am5.color(0xffffff), strokeWidth: 1 }
    : { fill: am5.color(0xffffff), fillOpacity: 0.6, stroke: am5.color(0x000000), strokeOpacity: 0.6, strokeWidth: 1.5 };
}

/**
 * The marker for one entry in the breakthrough strip. A plain breakthrough is a diamond; one that is also a Race or
 * Event is a diamond edged in the medal's colour (or dark grey) with the tag's target inside it; a Race or Event on
 * its own is a target (rings and a dot in the tag's colour). `size` is the diamond's side.
 */
function breakthroughSprite(root: any, item: { medal?: string; url?: string; kind?: 'Race' | 'Event'; eventOnly?: boolean }, size: number, pointer: Record<string, unknown>): any {
  const { am5 } = getTypedAm5();
  const settings = { cursorOverStyle: item.url ? 'pointer' : 'default', ...pointer };
  const open = (sprite: any) => sprite.events.on('click', () => item.url && window.open(item.url, '_blank', 'noopener'));
  if (item.eventOnly && item.kind) {
    const color = am5.color(RACE_EVENT_COLORS[item.kind]);
    const radius = Math.max(7, size * 0.6);
    // Only the outer disc takes the pointer; the inner ring and dot are drawn over it.
    const disc = am5.Circle.new(root, { radius, fill: am5.color(0xffffff), stroke: color, strokeWidth: 1.5, ...settings });
    open(disc);
    const target = am5.Container.new(root, {});
    target.children.push(disc);
    target.children.push(am5.Circle.new(root, { radius: radius * 0.55, fill: am5.color(0xffffff), stroke: color, strokeWidth: 1.5 }));
    target.children.push(am5.Circle.new(root, { radius: radius * 0.2, fill: color }));
    return target;
  }
  if (item.kind) {
    // A breakthrough that is also a Race/Event: the diamond, edged in the medal's colour (or dark grey), with the
    // tag's target drawn as diamonds inside it: a ring and a dot.
    const color = am5.color(RACE_EVENT_COLORS[item.kind]);
    const diamond = (side: number, look: Record<string, unknown>) =>
      am5.Rectangle.new(root, { width: side, height: side, rotation: 45, centerX: am5.p50, centerY: am5.p50, ...look });
    const outer = diamond(size, { fill: am5.color(0xffffff), fillOpacity: 1, stroke: item.medal ? am5.color(item.medal) : am5.color('#4B5563'), strokeWidth: 3, ...settings });
    open(outer);
    const target = am5.Container.new(root, {});
    target.children.push(outer);
    target.children.push(diamond(size * 0.6, { fill: am5.color(0xffffff), stroke: color, strokeWidth: 1.5 }));
    target.children.push(diamond(size * 0.25, { fill: color }));
    return target;
  }
  const diamond = am5.Rectangle.new(root, { width: size, height: size, rotation: 45, centerX: am5.p50, centerY: am5.p50, ...diamondLook(item), ...settings });
  open(diamond);
  return diamond;
}

const MEDAL_NAMES: Record<string, string> = { '#ffd700': 'Gold', '#c0c0c0': 'Silver', '#cd7f32': 'Bronze' };
/** "Gold Breakthrough", "Bronze Breakthrough"..., or "Near Breakthrough" when it didn't earn a medal. */
const breakthroughTitle = (item: { medal?: string }) =>
  item.medal ? `${MEDAL_NAMES[item.medal.toLowerCase()] ?? ''} Breakthrough`.trim() : 'Near Breakthrough';

/** A white tooltip with a thin dark-grey rounded border, for the HTML cards shown over diamonds and markers. */
function cardTooltip(root: any) {
  const { am5 } = getTypedAm5();
  const tooltip = am5.Tooltip.new(root, { getFillFromSprite: false, getStrokeFromSprite: false, autoTextColor: false, pointerOrientation: 'vertical' });
  (tooltip.get('background') as any).setAll({ fill: am5.color(0xffffff), fillOpacity: 1, stroke: am5.color('#4B5563'), strokeWidth: 1, strokeOpacity: 1, cornerRadius: 8 });
  tooltip.label.setAll({ fill: am5.color('#111827'), fontSize: 12 });
  return tooltip;
}

function breakthroughTooltipSettings(root: any, item: any) {
  const { tooltip, html } = breakthroughTooltip(root, item);
  return { tooltip, tooltipHTML: html };
}

const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * The tooltip a breakthrough diamond shows: white with a thin dark-grey rounded border, a trophy in
 * the medal's colour (grey for a near breakthrough), the kind of breakthrough, the activity, and
 * how long the maximal effort was.
 */
/** `TP: 391 +4  HIE: 35.8 −0.4  PP: 1,358 +12`: the signature, and how far this breakthrough moved it, in blue / amber / red. */
function signatureRow(sig: Signature, previous?: Signature): string {
  const signed = (change: number, decimals: number) => {
    const rounded = Number(change.toFixed(decimals));
    return rounded > 0 ? `+${rounded.toLocaleString()}` : rounded < 0 ? `−${Math.abs(rounded).toLocaleString()}` : '0';
  };
  const part = (label: string, colour: string, value: number, before: number | undefined, decimals: number) =>
    `<span style="color:${colour};font-weight:600">${label}: ${value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${before == null ? '' : ' ' + signed(value - before, decimals)}</span>`;
  return [
    part('TP', xssColors.xlss, sig.ftp, previous?.ftp, 0),
    part('HIE', '#B58900', sig.hie, previous?.hie, 1),
    part('PP', xssColors.xpss, sig.pp, previous?.pp, 0),
  ].join(' &nbsp;');
}

function breakthroughTooltip(root: any, item: { medal?: string; name?: string; desc?: string; mep?: number; sig?: Signature; sigPrev?: Signature; kind?: string; eventOnly?: boolean; date?: string }) {
  const { am5 } = getTypedAm5();
  const trophy = item.medal ?? '#9CA3AF';
  // A race or event that is not a breakthrough: its kind, the activity, and the day.
  if (item.eventOnly) {
    const day = item.date ? new Date(`${item.date}T12:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';
    return {
      tooltip: cardTooltip(root),
      html: `<div style="font-size:12px;line-height:1.45;color:#111827"><div style="font-weight:600">${escapeHtml(item.kind ?? '')}</div>${item.name ? `<div>${escapeHtml(item.name)}</div>` : ''}${day ? `<div style="color:#4B5563">${day}</div>` : ''}</div>`,
    };
  }
  const lines = [
    item.kind ? `<div style="font-weight:600">${escapeHtml(item.kind)}</div>` : '',
    `<div style="font-weight:600;white-space:nowrap"><i class="fa-solid fa-trophy" style="color:${escapeHtml(trophy)};margin-right:6px"></i>${escapeHtml(breakthroughTitle(item))}</div>`,
    item.name ? `<div>${escapeHtml(item.name)}</div>` : '',
    // The signature it left (and its change) in place of the activity's own description line.
    item.sig ? `<div style="white-space:nowrap">${signatureRow(item.sig, item.sigPrev)}</div>` : item.desc ? `<div style="color:#4B5563">${escapeHtml(item.desc)}</div>` : '',
    item.mep ? `<div style="color:#4B5563">Maximal effort: ${minutesSeconds(item.mep)}</div>` : '',
  ];
  const tooltip = cardTooltip(root);
  return { tooltip, html: `<div style="font-size:12px;line-height:1.45;color:#111827">${lines.join('')}</div>` };
}

/** A hidden 0-1 scale of its own: markers and the tooltip carrier sit at the same height whichever scales are showing. */
export function addEventsAxis(root: any, chart: any): any {
  const { am5xy } = getTypedAm5();
  const axis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}), min: 0, max: 1, strictMinMax: true })
  );
  axis.set('forceHidden', true);
  return axis;
}

/** Breakthrough diamonds along the bottom, gold/silver/bronze when a medal was earned; click opens the activity. */
export function addBreakthroughSeries(root: any, chart: any, dateAxis: any, eventsAxis: any, marks: ChartBreakthrough[]): any {
  const { am5, am5xy } = getTypedAm5();
  const marker = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: 'Breakthroughs',
      xAxis: dateAxis,
      yAxis: eventsAxis,
      valueXField: 't',
      valueYField: '_y',
    })
  );
  marker.strokes.template.set('visible', false);
  marker.bullets.push((_root: any, _series: any, dataItem: any) => {
    const item = dataItem.dataContext as { medal?: string; url?: string; kind?: 'Race' | 'Event'; eventOnly?: boolean };
    const sprite = breakthroughSprite(root, item, 10, breakthroughTooltipSettings(root, item));
    return am5.Bullet.new(root, { sprite });
  });
  marker.data.setAll(marks.map((mark) => ({ ...mark, _y: 0.05 })));
  return marker;
}

/**
 * One tooltip for the whole day, listing every series showing, and the cursor that snaps to it. The
 * carrier has a point on every day; `lines` gives the body of the tooltip for the day under the cursor.
 */
export function addDayTooltip(
  root: any,
  chart: any,
  dateAxis: any,
  eventsAxis: any,
  days: ChartDay[],
  lines: (day: ChartDay) => string[],
  onSelect?: (from: number, to: number) => void,
  cursorSettings: Record<string, unknown> = {},
  /** Lays the body of the tooltip out as HTML (a table, say) rather than as lines of text. Replaces `lines`. */
  htmlBody?: (day: ChartDay) => string
): any {
  const { am5, am5xy } = getTypedAm5();
  const carrier = chart.series.push(
    am5xy.LineSeries.new(root, {
      xAxis: dateAxis,
      yAxis: eventsAxis,
      valueXField: 't',
      valueYField: '_y',
      tooltip: am5.Tooltip.new(root, { pointerOrientation: 'horizontal', getFillFromSprite: false, ...(htmlBody ? { labelHTML: ' ' } : {}) }),
      tooltipText: ' ',
    })
  );
  carrier.strokes.template.set('visible', false);
  carrier.data.setAll(days.map((day) => ({ ...day, _y: 0.5 })));
  carrier.get('tooltip').get('background').setAll({ fill: am5.color(0xffffff), fillOpacity: 0.95, stroke: am5.color(0xd1d5db) });
  carrier.get('tooltip').label.setAll({ fill: am5.color(0x111827), fontSize: 12, textAlign: 'start' });
  if (htmlBody) {
    carrier.get('tooltip').label.adapters.add('html', (html: any) => {
      const day = carrier.get('tooltipDataItem')?.dataContext as ChartDay | undefined;
      return day ? htmlBody(day) : html;
    });
  }
  carrier.get('tooltip').label.adapters.add('text', (text: any) => {
    const day = carrier.get('tooltipDataItem')?.dataContext as ChartDay | undefined;
    if (!day) return text;
    const heading = new Date(day.t).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    return [`[bold]${heading}[/]`, ...lines(day)].join('\n');
  });
  addSelectionCursor(root, chart, dateAxis, onSelect, { snapToSeries: [carrier], snapToSeriesBy: 'x', ...cursorSettings });
  return carrier;
}

/**
 * The cursor of a chart. Dragging across it marks out a stretch of time without moving the chart's
 * time axis, and letting go reports the start and end of it (as times on the axis) to `onSelect`.
 */
export function addSelectionCursor(
  root: any,
  chart: any,
  dateAxis: any,
  onSelect?: (from: number, to: number) => void,
  cursorSettings: Record<string, unknown> = {}
): any {
  const { am5, am5xy } = getTypedAm5();
  chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'selectX', ...cursorSettings }));
  const cursor = chart.get('cursor');
  cursor.lineY.set('visible', false);
  cursor.selection.setAll({ fill: am5.color('#0B5DBC'), fillOpacity: 0.12, stroke: am5.color('#0B5DBC'), strokeOpacity: 0.5, strokeWidth: 1 });
  cursor.events.on('selectended', () => {
    const down = cursor.getPrivate('downPositionX');
    const up = cursor.getPrivate('positionX');
    // A click, or a nudge of the mouse, isn't a selection.
    if (down == null || up == null || Math.abs(up - down) * chart.plotContainer.width() < 8) return;
    const a = dateAxis.positionToValue(dateAxis.toAxisPosition(down));
    const b = dateAxis.positionToValue(dateAxis.toAxisPosition(up));
    onSelect?.(Math.min(a, b), Math.max(a, b));
  });
  return cursor;
}

/**
 * The roots let tooltips run past the chart's edges (tooltipContainerBounds), which on a phone, where the chart sits a
 * few pixels from the screen edge, pushes a tooltip for something near the chart's left or right end off the screen.
 * Tooltips read the tooltip container's layerMargin each time they're placed, so trim its sides to the room there is.
 */
export function keepTooltipsOnScreen(root: any, el: HTMLElement) {
  const gap = 4;
  root.tooltipContainer?.adapters.add('layerMargin', (margin: any) => {
    if (!margin) return margin;
    const rect = el.getBoundingClientRect();
    return {
      ...margin,
      left: Math.max(0, Math.min(margin.left ?? 0, rect.left - gap)),
      right: Math.max(0, Math.min(margin.right ?? 0, window.innerWidth - rect.right - gap)),
    };
  });
}

/** "Today" guide, when today is inside what's drawn. */
export function addTodayGuide(dateAxis: any, days: ChartDay[], today: string, showLabel = true, topInset = 0) {
  const { am5 } = getTypedAm5();
  const todayTime = today ? dateToChartTime(today) : NaN;
  if (!days.length || !(todayTime >= days[0].t && todayTime <= days[days.length - 1].t)) return;
  const guide = dateAxis.createAxisRange(dateAxis.makeDataItem({ value: todayTime }));
  guide.get('grid').setAll({
    stroke: am5.color(0x111827),
    strokeOpacity: 0.6,
    strokeWidth: 1,
    strokeDasharray: [3, 3],
    visible: true,
  });
  guideLabel(dateAxis, guide, 'Today', '#111827', showLabel, topInset);
}

/**
 * A guide's label, drawn along its line at the top of the plot (rotated -90 degrees, on its left), so labels of guides that are
 * close together (Today and a program start a few days later) don't run into each other the way labels side by side
 * under the axis did. Axis-range labels sit on the axis, so it is lifted by the plot's height, kept in step on resize.
 * topInset starts it that many pixels lower, clear of a title in the plot's top-left corner.
 */
function guideLabel(dateAxis: any, guide: any, text: string, color: string, showLabel: boolean, topInset = 0) {
  const { am5 } = getTypedAm5();
  const plot = dateAxis.chart?.plotContainer;
  const label = guide.get('label');
  label.setAll({
    text,
    visible: showLabel,
    // Range labels inherit the renderer template's forceHidden, which is on for charts that don't show their own
    // date labels (the upper Fitness Signatures panels), so it has to be undone here.
    forceHidden: !showLabel,
    inside: true,
    rotation: -90,
    // Reading up the line, ending at its top, just to its left.
    centerX: am5.p100,
    centerY: am5.p100,
    paddingTop: 4,
    paddingBottom: 2,
    paddingLeft: 0,
    paddingRight: 0,
    fontSize: 11,
    fontWeight: '600',
    fill: am5.color(color),
    background: am5.Rectangle.new(dateAxis.root, { fill: am5.color(0xffffff), fillOpacity: 0.75 }),
  });
  if (!plot) return;
  label.adapters.add('dy', () => -plot.height() + topInset);
  plot.events.on('boundschanged', () => label.markDirtyPosition());
}

/**
 * "XFAI Program Start" guide: where a Forecast AI plan with a chosen start date begins (or will begin, during its
 * Pre-Plan Maintenance Program), when that date is inside what's drawn.
 */
export function addProgramStartGuide(dateAxis: any, days: ChartDay[], programStart: string, showLabel = true, topInset = 0) {
  const { am5 } = getTypedAm5();
  const startTime = programStart ? dateToChartTime(programStart) : NaN;
  if (!days.length || !(startTime >= days[0].t && startTime <= days[days.length - 1].t)) return;
  const guide = dateAxis.createAxisRange(dateAxis.makeDataItem({ value: startTime }));
  guide.get('grid').setAll({
    stroke: am5.color('#0B5DBC'),
    strokeOpacity: 0.7,
    strokeWidth: 1.5,
    strokeDasharray: [5, 3],
    visible: true,
  });
  guideLabel(dateAxis, guide, 'XFAI Program Start', '#0B5DBC', showLabel, topInset);
}

/** `m:ss`, for a length of time in seconds. */
const minutesSeconds = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;

/**
 * Breakthrough diamonds for a strip of their own above the main chart, each sized by how long the
 * athlete held that maximal effort (the activity's MEP). The strip has one hidden 0-1 scale.
 */
export function addBreakthroughDiamonds(root: any, strip: any, dateAxis: any, marks: ChartBreakthrough[]): any {
  const { am5, am5xy } = getTypedAm5();
  const yAxis = strip.yAxes.push(
    am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}), min: 0, max: 1, strictMinMax: true })
  );
  yAxis.set('forceHidden', true);
  const series = strip.series.push(
    am5xy.LineSeries.new(root, { name: 'Breakthroughs', xAxis: dateAxis, yAxis, valueXField: 't', valueYField: '_y' })
  );
  series.strokes.template.set('visible', false);

  // Area follows the effort: side grows with its square root, from a size that is still easy to see.
  const longest = marks.reduce((max, mark) => Math.max(max, mark.mep ?? 0), 0);
  const sizeOf = (mep?: number) => (mep && longest > 0 ? 8 + 16 * Math.sqrt(mep / longest) : 12);
  series.bullets.push((_root: any, _series: any, dataItem: any) => {
    const item = dataItem.dataContext as { medal?: string; url?: string; size: number; kind?: 'Race' | 'Event'; eventOnly?: boolean };
    const sprite = breakthroughSprite(root, item, item.size, breakthroughTooltipSettings(root, item));
    return am5.Bullet.new(root, { sprite });
  });
  series.data.setAll(
    marks.map((mark) => ({
      ...mark,
      _y: 0.5,
      size: sizeOf(mark.mep),
      effort: mark.mep ? `Maximal effort: ${minutesSeconds(mark.mep)}` : '',
    }))
  );
  return series;
}


// -- Flagged / manually set activities ------------------------------------------------------------

/** Font Awesome (solid) shapes for the icons in the strip, drawn directly so they don't depend on the icon font being loaded. */
const ICON_SIZE_PX = 14;
const MARKER_ICONS: Record<MarkerKind, { path: string; size: number }> = {
  flagged: {
    size: 512,
    path: 'M64 32C64 14.3 49.7 0 32 0S0 14.3 0 32V64 368 480c0 17.7 14.3 32 32 32s32-14.3 32-32V352l64.3-16.1c41.1-10.3 84.6-5.5 122.5 13.4c44.2 22.1 95.5 24.8 141.7 7.4l34.7-13c12.5-4.7 20.8-16.6 20.8-30V66.1c0-23-24.2-38-44.8-27.7l-9.6 4.8c-46.3 23.2-100.8 23.2-147.1 0c-35.1-17.6-75.4-22-113.5-12.5L64 48V32z',
  },
  signature: {
    size: 512,
    path: 'M144 144v48H304V144c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192V144C80 64.5 144.5 0 224 0s144 64.5 144 144v48h16c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V256c0-35.3 28.7-64 64-64H80z',
  },
  tl: {
    size: 512,
    path: 'M0 416c0-17.7 14.3-32 32-32l54.7 0c12.3-28.3 40.5-48 73.3-48s61 19.7 73.3 48L480 384c17.7 0 32 14.3 32 32s-14.3 32-32 32l-246.7 0c-12.3 28.3-40.5 48-73.3 48s-61-19.7-73.3-48L32 448c-17.7 0-32-14.3-32-32zm192 0a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zM384 256a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm-32-80c32.8 0 61 19.7 73.3 48l54.7 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-54.7 0c-12.3 28.3-40.5 48-73.3 48s-61-19.7-73.3-48L32 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l246.7 0c12.3-28.3 40.5-48 73.3-48zM192 64a32 32 0 1 0 0 64 32 32 0 1 0 0-64zm73.3 0L480 64c17.7 0 32 14.3 32 32s-14.3 32-32 32l-214.7 0c-12.3 28.3-40.5 48-73.3 48s-61-19.7-73.3-48L32 128C14.3 128 0 113.7 0 96S14.3 64 32 64l86.7 0C131 35.7 159.2 16 192 16s61 19.7 73.3 48z',
  },
  // Font Awesome Pro solid note-sticky.
  note: {
    size: 512,
    path: 'M64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l224 0 0-112c0-26.5 21.5-48 48-48l112 0 0-224c0-35.3-28.7-64-64-64L64 32zM448 352l-45.3 0L336 352c-8.8 0-16 7.2-16 16l0 66.7 0 45.3 32-32 64-64 32-32z',
  },
};

const MARKER_LOOK: Record<MarkerKind, { icon: string; title: string; note: string }> = {
  flagged: {
    icon: 'fa-flag',
    title: 'Flagged activity',
    note: 'Any breakthrough from this activity is flagged as invalid, so its signature changes were not applied. Its XSS still counts.',
  },
  signature: {
    icon: 'fa-lock',
    title: 'Signature set manually',
    note: 'Held as set, rather than worked out from your rides, until the manual signature is removed.',
  },
  tl: {
    icon: 'fa-sliders',
    title: 'Training load set manually',
    note: 'Your training and recovery load carry on from these values.',
  },
  // note is unused for this kind: markerTooltipSettings shows the note's own text instead (see below).
  note: {
    icon: 'fa-note-sticky',
    title: 'Note',
    note: '',
  },
};

/** A training load set by an XFAI plan (its pre-plan TL/RL preset), not by the athlete. */
const XFAI_TL_LOOK = {
  icon: MARKER_LOOK.tl.icon,
  title: 'Training load set by XFAI Program',
  note: 'The system is expecting you to maintain your fitness until the start of the program.',
};

/** The tooltip an activity marker shows: what was set on that activity, and what that means for the charts. */
function markerTooltipSettings(root: any, kind: MarkerKind, marker: ChartMarker) {
  const look = kind === 'tl' && marker.tlSource === 'xfai' ? XFAI_TL_LOOK : MARKER_LOOK[kind];
  const colour = MARKER_KINDS[kind].color;
  const values =
    kind === 'signature' && marker.sig
      ? signatureRow(marker.sig)
      : kind === 'tl' && marker.tl
        ? [
            ['Low', xssColors.xlss, marker.tl.low],
            ['High', '#B58900', marker.tl.high],
            ['Peak', xssColors.xpss, marker.tl.peak],
          ]
            .map(([label, tint, value]) => `<span style="color:${tint};font-weight:600">${label}: ${(value as number).toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>`)
            .join(' &nbsp;')
        : '';
  const date = new Date(marker.t).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  // A note's own text stands in for the fixed explanatory blurb the other kinds show.
  const noteText = kind === 'note' && marker.note ? escapeHtml(marker.note) : look.note;
  const html = `<div style="font-size:12px;line-height:1.45;color:#111827;max-width:280px;white-space:normal">
    <div style="font-weight:600;white-space:nowrap"><i class="fa-solid ${look.icon}" style="color:${colour};margin-right:6px"></i>${look.title}</div>
    ${marker.name ? `<div>${escapeHtml(marker.name)}</div>` : ''}
    <div style="color:#4B5563">${escapeHtml(date)}</div>
    ${values ? `<div style="white-space:nowrap;margin-top:2px">${values}</div>` : ''}
    <div style="color:#4B5563;margin-top:2px;white-space:pre-wrap">${noteText}</div>
  </div>`;
  return { tooltip: cardTooltip(root), tooltipHTML: html };
}

/**
 * The strip of flagged / manually set activities above the signature charts: one lane for each kind, and a
 * mark for each activity in it. Returns the series of each kind (for switching a kind on and off).
 * `hover` is told which marker the pointer is on (or null), so its guideline can be brought out.
 */
export function addMarkerLanes(
  root: any,
  strip: any,
  dateAxis: any,
  markers: ChartMarker[],
  hover: (index: number | null) => void,
  kinds: MarkerKind[] = MARKER_KIND_ORDER
): Partial<Record<MarkerKind, any>> {
  const { am5, am5xy } = getTypedAm5();
  // max 1.3, not 1: note's lane (1.1) sits above flagged's (0.8, the previous top), with the same ~0.3 spacing and
  // headroom the other lanes have, rather than being flush against the strip's edge. A strip of one kind (the XPMC
  // chart's notes) centres that kind's lane instead.
  const max = kinds.length === 1 ? 2 * MARKER_KINDS[kinds[0]].lane : 1.3;
  const yAxis = strip.yAxes.push(
    am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}), min: 0, max, strictMinMax: true })
  );
  yAxis.set('forceHidden', true);

  const lanes: Partial<Record<MarkerKind, any>> = {};
  for (const kind of kinds) {
    const items = markers.filter((marker) => marker.kinds.includes(kind));
    if (!items.length) continue;
    const cfg = MARKER_KINDS[kind];
    const series = strip.series.push(
      am5xy.LineSeries.new(root, { name: cfg.label, xAxis: dateAxis, yAxis, valueXField: 't', valueYField: '_y' })
    );
    series.strokes.template.set('visible', false);
    series.bullets.push((_root: any, _series: any, dataItem: any) => {
      const marker = dataItem.dataContext as ChartMarker;
      const look = {
        fill: am5.color(cfg.color),
        fillOpacity: 0.9,
        stroke: am5.color(0xffffff),
        strokeWidth: 1,
        cursorOverStyle: marker.url ? 'pointer' : 'default',
        ...markerTooltipSettings(root, kind, marker),
      };
      // A flag for a flagged activity, a lock for a set signature, sliders for a set load: the same icons as the
      // tooltips, and readable without the colour.
      const icon = MARKER_ICONS[kind];
      const sprite = am5.Graphics.new(root, {
        svgPath: icon.path,
        scale: ICON_SIZE_PX / icon.size,
        centerX: am5.p50,
        centerY: am5.p50,
        ...look,
        strokeWidth: 0,
      });
      sprite.events.on('click', () => {
        if (marker.url) window.open(marker.url, '_blank', 'noopener');
      });
      sprite.events.on('pointerover', () => hover(marker.index));
      sprite.events.on('pointerout', () => hover(null));
      return am5.Bullet.new(root, { sprite });
    });
    series.data.setAll(items.map((marker) => ({ ...marker, _y: cfg.lane })));
    lanes[kind] = series;
  }
  return lanes;
}

/**
 * Signature = p0 + k1 * (its load), the model behind the fitness signature, so a load scale and a signature scale
 * are colinear when the signature scale starts at p0 and rises k1 for every unit of load. Returns both ranges, with
 * the load scale widened where needed so that neither scale clips what it carries.
 */
export const colinearRanges = (
  load: { min: number; max: number },
  sig: { min: number; max: number },
  irParam: { p0?: number; k1?: number } | null | undefined,
  mode: ColinearMode = colinearMode()!
): { load: { min: number; max: number }; sig: { min: number; max: number } } | null => {
  // Alternative: each scale runs from the lowest to the highest value it carries, so both lines span the plot.
  if (mode === 'minmax') return load.max > load.min && sig.max > sig.min ? { load, sig } : null;
  const p0 = irParam?.p0;
  const k1 = irParam?.k1;
  if (typeof p0 !== 'number' || typeof k1 !== 'number' || !isFinite(p0) || !isFinite(k1) || k1 <= 0) return null;
  // The load scale starts at zero and ends at its top value; the signature scale follows the line from there, but is
  // widened wherever the signature (or the lower threshold) would otherwise run off an end of it.
  const loadMin = Math.min(0, load.min);
  const loadMax = load.max;
  if (!(loadMax > loadMin)) return null;
  return { load: { min: loadMin, max: loadMax }, sig: { min: Math.min(sig.min, p0 + k1 * loadMin), max: Math.max(sig.max, p0 + k1 * loadMax) } };
};

export type ColinearMode = 'ls' | 'fit' | 'p0' | 'minmax';

/** Which way the load and signature scales are lined up: `ls` (default), `fit`, `p0` or `minmax`; `?colinear=` in the address switches it, for comparing. */
export const colinearMode = (onlyForced = false): ColinearMode | null => {
  try {
    const mode = new URLSearchParams(window.location.search).get('colinear');
    return mode === 'ls' || mode === 'fit' || mode === 'p0' || mode === 'minmax' ? mode : onlyForced ? null : 'ls';
  } catch {
    return onlyForced ? null : 'ls';
  }
};

/**
 * p0 is a lower bound over the athlete's whole history, so the signature normally sits a fixed amount above
 * p0 + k1 * load. The `fit` mode keeps k1 but takes the intercept from the days drawn: the lowest of signature - k1 * load.
 */
export const fitIrParam = (
  irParam: { p0?: number; k1?: number } | null | undefined,
  days: Record<string, any>[],
  sigField: string,
  loadField: string,
  mode: ColinearMode = colinearMode()!
): { p0?: number; k1?: number } | null | undefined => {
  const k1 = irParam?.k1;
  if (mode === 'ls') {
    // The straight line through the days drawn (least squares), whose intercept and slope the scales take: the load scale
    // starts at zero and the signature scale at the line's value there.
    const points: [number, number][] = [];
    for (const day of days) {
      const x = day[loadField];
      const y = day[sigField];
      if (typeof x === 'number' && typeof y === 'number' && isFinite(x) && isFinite(y)) points.push([x, y]);
    }
    if (points.length > 2) {
      // Centred on the means, so near-constant loads don't produce a slope out of rounding error.
      const mx = points.reduce((sum, [x]) => sum + x, 0) / points.length;
      const my = points.reduce((sum, [, y]) => sum + y, 0) / points.length;
      let sxx = 0;
      let sxy = 0;
      for (const [x, y] of points) {
        sxx += (x - mx) * (x - mx);
        sxy += (x - mx) * (y - my);
      }
      if (sxx > 1e-9 * points.length * Math.max(1, mx * mx)) {
        const slope = sxy / sxx;
        if (slope > 0) return { p0: my - slope * mx, k1: slope };
      }
    }
    return fitIrParam(irParam, days, sigField, loadField, 'fit');
  }
  if (mode !== 'fit' || typeof k1 !== 'number') return irParam;
  let p0 = Infinity;
  for (const day of days) {
    const sig = day[sigField];
    const load = day[loadField];
    if (typeof sig === 'number' && typeof load === 'number' && isFinite(sig) && isFinite(load)) p0 = Math.min(p0, sig - k1 * load);
  }
  return isFinite(p0) ? { ...irParam, p0 } : irParam;
};

/** The load and signature scales to draw: the load scale from zero, the signature scale along the line that best fits the days drawn (or the mode `?colinear=` forces). */
export const colinearScales = (
  load: { min: number; max: number },
  sig: { min: number; max: number },
  irParam: { p0?: number; k1?: number } | null | undefined,
  days: Record<string, any>[],
  sigField: string,
  loadField: string
) => {
  const forced = colinearMode(true);
  const candidate = (mode: ColinearMode) => colinearRanges(load, sig, fitIrParam(irParam, days, sigField, loadField, mode), mode);
  if (forced) return candidate(forced);
  return candidate('ls');
};
